import * as React from "react";
import { Check } from "lucide-react";

import { cn } from "../../lib/utils";

/**
 * shadcn/ui Checkbox (new-york), without @radix-ui/react-checkbox.
 *
 * Adding the Radix package makes yarn re-resolve dozens of unrelated entries in
 * yarn.lock, so this is the same component on a plain <button role="checkbox">.
 * It keeps the Radix API (`checked`, `onCheckedChange`, `data-state`), so it can
 * be swapped for the stock file later without touching callers. A <label
 * htmlFor> pointing at its id toggles it, as with Radix.
 *
 * The tick is always rendered and faded/scaled in, rather than mounted, so a
 * quick double toggle retargets the transition instead of restarting it.
 */
const Checkbox = React.forwardRef(
  ({ className, checked = false, onCheckedChange, disabled, onClick, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      role="checkbox"
      aria-checked={checked}
      data-state={checked ? "checked" : "unchecked"}
      disabled={disabled}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) onCheckedChange?.(!checked);
      }}
      className={cn(
        "peer flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-primary shadow",
        "transition-colors duration-150 ease-out",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
        "disabled:cursor-not-allowed disabled:opacity-50",
        "data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground",
        className
      )}
      {...props}
    >
      <Check
        aria-hidden="true"
        strokeWidth={3}
        className={cn(
          "h-3 w-3 transition-[opacity,transform] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]",
          "motion-reduce:transition-opacity",
          checked ? "scale-100 opacity-100" : "scale-75 opacity-0"
        )}
      />
    </button>
  )
);
Checkbox.displayName = "Checkbox";

export { Checkbox };
